import { useCallback, useRef, useState } from "react";
import { CommandScope, CreateKind, QuickDraft } from "./types";
import { isCommandScopeCurrent } from "./commandSession";

export const DRAFT_PREFIX = "easyfleet:command-drafts:v1:";
const kinds: CreateKind[] = [
  "vendor",
  "vehicle",
  "workOrder",
  "serviceReminder",
  "vehicleReminder",
  "contactReminder",
];
export type CommandDrafts = Partial<Record<CreateKind, QuickDraft>>;
export const draftStorageKey = (scopeKey: string) =>
  DRAFT_PREFIX + encodeURIComponent(scopeKey);

export function readCommandDrafts(scopeKey: string): CommandDrafts {
  try {
    const raw = JSON.parse(
      localStorage.getItem(draftStorageKey(scopeKey)) || "{}",
    );
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.fromEntries(
      kinds
        .filter(
          (kind) =>
            raw[kind] &&
            typeof raw[kind] === "object" &&
            !Array.isArray(raw[kind]),
        )
        .map((kind) => [kind, raw[kind]]),
    );
  } catch {
    return {};
  }
}

export function clearCommandDrafts() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(DRAFT_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    /* Logout still clears in-memory state even if browser storage is unavailable. */
  }
}

/** Mounted with the authoritative user/company scope as its React key. */
export function useCommandDrafts(scope: CommandScope) {
  const [drafts, setDrafts] = useState<CommandDrafts>(() =>
    readCommandDrafts(scope.key),
  );
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const [storageError, setStorageError] = useState<string | null>(null);
  const update = useCallback(
    (kind: CreateKind, draft?: QuickDraft) => {
      // Ignore a late blur/save callback after logout; never recreate discarded storage.
      if (!isCommandScopeCurrent(scope)) return;
      let next = { ...draftsRef.current };
      try {
        // Read current browser state before writing: another tab may have saved
        // or discarded an unrelated form since this component mounted.
        localStorage.getItem(draftStorageKey(scope.key));
        next = readCommandDrafts(scope.key);
      } catch {
        /* Retain in-memory drafts when storage access is blocked. */
      }
      if (draft) next[kind] = draft;
      else delete next[kind];
      draftsRef.current = next;
      setDrafts(next);
      try {
        localStorage.setItem(draftStorageKey(scope.key), JSON.stringify(next));
        setStorageError(null);
      } catch {
        setStorageError(
          "Drafts are kept while this page stays open. Browser storage is unavailable, so refreshing will lose them.",
        );
      }
    },
    [scope],
  );
  const onDraftChange = useCallback(
    (kind: CreateKind, draft: QuickDraft) => update(kind, draft),
    [update],
  );
  const onDiscardDraft = useCallback(
    (kind: CreateKind) => update(kind),
    [update],
  );
  return { drafts, onDraftChange, onDiscardDraft, storageError };
}
